import type { TrailerBodyStyle, TrailerHitch, TruckBodyStyle, TruckCoupling, TyreLayoutSubjectKind } from '@chukua/common';

/** Every detail either catalogue has; a truck class uses capacity and licence, a trailer type its container sizes. */
export interface VehicleClassDetailsValue {
  code?: string;
  name?: string;
  bodyStyle?: TruckBodyStyle | TrailerBodyStyle;
  typicalCapacityTonnes?: number | null;
  requiredLicenceClass?: string | null;
  defaultKmPerLitre?: number | null;
  containerSizeCodes?: string[];
  coupling?: TruckCoupling;
  hitch?: TrailerHitch;
}

interface DetailsSource {
  code: string;
  name: string;
  bodyStyle: TruckBodyStyle | TrailerBodyStyle;
  typicalCapacityTonnes?: number;
  requiredLicenceClass?: string | null;
  defaultKmPerLitre?: number | null;
  containerSizeCodes?: string[];
  coupling?: TruckCoupling;
  hitch?: TrailerHitch;
}

const BLANK: Record<TyreLayoutSubjectKind, VehicleClassDetailsValue> = {
  truck_class: {
    code: '',
    name: '',
    bodyStyle: 'tractor',
    typicalCapacityTonnes: null,
    requiredLicenceClass: null,
    defaultKmPerLitre: null,
    coupling: 'fifth_wheel',
  },
  trailer_type: { code: '', name: '', bodyStyle: 'skeletal_40', containerSizeCodes: [], hitch: 'kingpin' },
};

/** The form's starting value: the row's own details, or a blank one of its kind. */
export function detailsFormValue(kind: TyreLayoutSubjectKind, subject: DetailsSource | null): VehicleClassDetailsValue {
  if (!subject) return BLANK[kind];
  if (kind === 'truck_class') {
    const { code, name, bodyStyle, typicalCapacityTonnes, requiredLicenceClass, defaultKmPerLitre, coupling } = subject;
    return {
      code,
      name,
      bodyStyle,
      typicalCapacityTonnes: typicalCapacityTonnes ?? null,
      requiredLicenceClass: requiredLicenceClass ?? null,
      defaultKmPerLitre: defaultKmPerLitre ?? null,
      coupling,
    };
  }
  return { code: subject.code, name: subject.name, bodyStyle: subject.bodyStyle, containerSizeCodes: subject.containerSizeCodes ?? [], hitch: subject.hitch };
}

/** Whether the form differs from the saved row, empty text and a cleared value counting as the same. */
export function isDetailsChanged(kind: TyreLayoutSubjectKind, subject: DetailsSource | null, value: VehicleClassDetailsValue | undefined): boolean {
  if (!value) return false;
  const saved = detailsFormValue(kind, subject);
  const normalise = (v: VehicleClassDetailsValue) =>
    JSON.stringify(
      Object.keys(saved)
        .map((key) => v[key as keyof VehicleClassDetailsValue] ?? null)
        .map((field) => (field === '' ? null : field)),
    );
  return normalise(saved) !== normalise(value);
}

export type VehicleClassFormInput =
  | {
      code: string;
      name: string;
      bodyStyle: TruckBodyStyle;
      typicalCapacityTonnes: number;
      requiredLicenceClass: string | null;
      defaultKmPerLitre: number | null;
      coupling?: TruckCoupling;
    }
  | { code: string; name: string; bodyStyle: TrailerBodyStyle; containerSizeCodes: string[]; hitch?: TrailerHitch };

/** What the catalogue's create or update takes; an emptied licence class is cleared rather than kept. */
export function toVehicleClassInput(kind: TyreLayoutSubjectKind, value: VehicleClassDetailsValue): VehicleClassFormInput {
  const code = (value.code ?? '').trim();
  const name = (value.name ?? '').trim();
  if (kind === 'truck_class') return toTruckClassInput(code, name, value);
  return {
    code,
    name,
    bodyStyle: (value.bodyStyle ?? 'skeletal_40') as TrailerBodyStyle,
    containerSizeCodes: value.containerSizeCodes ?? [],
    hitch: value.hitch,
  };
}

function toTruckClassInput(code: string, name: string, value: VehicleClassDetailsValue): VehicleClassFormInput {
  return {
    code,
    name,
    bodyStyle: (value.bodyStyle ?? 'tractor') as TruckBodyStyle,
    typicalCapacityTonnes: value.typicalCapacityTonnes ?? 0,
    requiredLicenceClass: value.requiredLicenceClass?.trim() || null,
    defaultKmPerLitre: value.defaultKmPerLitre ?? null,
    coupling: value.coupling,
  };
}
